import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchGuideline,
  listFiles,
  listGuidelines,
  mailboxLabel,
  type ContentKind,
  type Direction,
  type Guideline,
  type Mailbox,
  type MailboxInfo,
} from "../api";
import { domainApi, domainEvents } from "../generated/api-surface.gen";
import { saveBlob } from "../download";
import { formatSize } from "../format";
import { FILE_KINDS, FILE_KIND_ORDER, NAME_PREFIXES, RECORD_TYPES } from "./catalogue";
import { PipelineDiagram } from "./PipelineDiagram";

// How the service works, beside the files it works on.
//
// Two things are true of every fact on this page: it is either GENERATED from a
// contract (the operation tables, the events), COUNTED from the buckets in front
// of you (how many of each kind are in the mailbox right now), or written down
// once here with the bank's own specification one click away. Nothing is a
// screenshot of how the system used to behave.

const SECTIONS = [
  { id: "overview", title: "What this is" },
  { id: "pipeline", title: "How a file moves" },
  { id: "files", title: "What is in the mailbox" },
  { id: "names", title: "Reading a file name" },
  { id: "records", title: "Reading a record" },
  { id: "api", title: "The API" },
  { id: "events", title: "The events" },
  { id: "dates", title: "Dates and deadlines" },
  { id: "guidelines", title: "The bank's guidelines" },
] as const;

export type SectionId = (typeof SECTIONS)[number]["id"];

/**
 * A request to open the page at one section — from the masthead, which offers
 * "Dates and deadlines" as a shortcut. The nonce is what makes a second click on
 * the same entry scroll again, after the reader has wandered off.
 */
export interface SectionRequest {
  section: SectionId;
  nonce: number;
}

const GROUP_TITLES: Record<Guideline["group"], string> = {
  delivery: "The record layouts",
  calendar: "The calendar",
  operations: "Running as a creditor",
  transport: "The SFTP transport",
};

const GROUP_ORDER: ReadonlyArray<Guideline["group"]> = ["delivery", "calendar", "operations", "transport"];

/** The breathing room left above a heading when the page is placed on it. */
const HEADING_GAP = 12;

/** Where the bank publishes each half-year's calendar. */
const CALENDAR_SOURCE = "https://www.betalingsservice.dk/betalingsservice/hjaelp/kalendere-tidsfrister/";

/** How many files of one kind one mailbox holds right now. */
interface MailboxCount {
  mailbox: MailboxInfo;
  count: number;
}

interface Counts {
  /** Per kind, one entry per mailbox that holds any. */
  byKind: Partial<Record<ContentKind, MailboxCount[]>>;
  inbound: number;
  outbound: number;
  state: "counting" | "counted" | "failed";
}

export function Documentation({
  token,
  mailboxes,
  onOpenFiles,
  onOpenReference,
  open,
}: {
  token: string;
  /** The logins enabled here; the counts are taken across all of them. */
  mailboxes: MailboxInfo[];
  onOpenFiles: (mailbox: Mailbox, direction: Direction, kind: ContentKind) => void;
  /** Opens the full API reference — every parameter, schema and event. */
  onOpenReference: () => void;
  /** Where to open the page, when something outside it asked for a section. */
  open?: SectionRequest;
}): JSX.Element {
  const [active, setActive] = useState<SectionId>("overview");
  const article = useRef<HTMLElement>(null);
  const counts = useLiveCounts(token, mailboxes);
  const guidelines = useGuidelines(token);

  // Which section the reader is in, so the contents list keeps up with a long
  // page. The top quarter of the viewport is the "you are here" line.
  useEffect(() => {
    const root = article.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible[0]) setActive(visible[0].target.id as SectionId);
      },
      { root, rootMargin: "0px 0px -75% 0px", threshold: 0 },
    );
    for (const section of SECTIONS) {
      const element = root.querySelector(`#${section.id}`);
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, []);

  // Positioned rather than scrollIntoView'd: a smooth scroll is dropped outright
  // when the tab is not the one being painted, which turns a contents link into a
  // button that does nothing. Landing on the heading always works.
  const goTo = useCallback((id: SectionId): void => {
    const root = article.current;
    const target = root?.querySelector(`#${id}`);
    if (!root || !target) return;
    root.scrollTop += target.getBoundingClientRect().top - root.getBoundingClientRect().top - HEADING_GAP;
  }, []);

  // The masthead's own entry into this page. Keyed on the nonce, so asking for
  // the same section twice scrolls to it twice.
  //
  // Arriving from the file list mounts this page and scrolls it in the same
  // breath — before the calendar image has arrived, while the page is still
  // shorter than it will be, so a single scroll lands short of a section near
  // the end. So the placement is repeated whenever a section changes size, until
  // the reader takes over or the page stops growing.
  useEffect(() => {
    const root = article.current;
    if (!open || !root) return;

    const place = (): void => goTo(open.section);
    const observer = new ResizeObserver(place);
    for (const section of Array.from(root.children)) observer.observe(section);

    const stop = (): void => {
      observer.disconnect();
      root.removeEventListener("wheel", stop);
      root.removeEventListener("touchstart", stop);
      window.clearTimeout(giveUp);
    };
    // A page that never stopped growing would keep yanking the reader back.
    const giveUp = window.setTimeout(stop, 5000);
    root.addEventListener("wheel", stop, { passive: true });
    root.addEventListener("touchstart", stop, { passive: true });

    place();
    return stop;
  }, [open?.nonce, open?.section, goTo]);

  return (
    <div className="workspace">
      <nav className="rail doc-rail" aria-label="Contents">
        <div className="doc-contents">
          <span className="label">Contents</span>
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              className="doc-link"
              aria-current={active === section.id}
              onClick={() => goTo(section.id)}
            >
              {section.title}
            </button>
          ))}
        </div>
        <div className="rail-foot">
          <span>{railStatus(counts)}</span>
        </div>
      </nav>

      <article className="detail doc" ref={article}>
        <Overview />
        <Pipeline />
        <Files counts={counts} mailboxes={mailboxes} onOpenFiles={onOpenFiles} />
        <Names />
        <Records />
        <Api onOpenReference={onOpenReference} />
        <Events onOpenReference={onOpenReference} />
        <Dates calendars={guidelines.items.filter((item) => item.group === "calendar")} token={token} />
        <Guidelines guidelines={guidelines} token={token} />
      </article>
    </div>
  );
}

// ── Sections ────────────────────────────────────────────────────────────────

function Overview(): JSX.Element {
  return (
    <section id="overview" className="doc-section">
      <h2>What this is</h2>
      <p className="doc-lede">
        <b>betalingsservice-api</b> is the single gateway to the Danish direct-debit rail. Callers work in
        domain terms — collections, mandates, debtors, outcomes — and never see a file, a record, or an SFTP
        connection. This interface is the other half of that bargain: the files still exist, and this is where
        you read them.
      </p>

      <div className="doc-facts">
        <div className="doc-fact">
          <h3>A pure executor</h3>
          <p>
            It carries no business rules that drift. Betalingsservice date rules — banking days, holidays,
            cutoffs — belong to the caller; a non-conforming due date is rejected by the bank and comes back as
            a rejected outcome. The only date check here is the stable one: not in the past.
          </p>
        </div>
        <div className="doc-fact">
          <h3>Stateless</h3>
          <p>
            No database. The only thing persisted is a small S3 idempotency ledger beside the outbound files,
            and every inbound result leaves as an EventBridge event rather than being stored.
          </p>
        </div>
        <div className="doc-fact">
          <h3>Idempotent submits</h3>
          <p>
            <code>deliveryReference</code> is the idempotency key and is required on every real submit. The
            outbound object is written with a conditional create, so re-sending a reference ships nothing new
            and replays the original <code>202</code>. A retry after a timeout cannot double-charge a payer.
          </p>
        </div>
        <div className="doc-fact">
          <h3>Strict inbound</h3>
          <p>
            Every fetched file is handled, a recognised no-op, or <b>refused</b> — a file nobody has taught the
            processor about throws, fails the Lambda and pages through Sentry rather than being dropped
            quietly. The last case is what this inspector exists for.
          </p>
        </div>
      </div>
    </section>
  );
}

function Pipeline(): JSX.Element {
  return (
    <section id="pipeline" className="doc-section">
      <h2>How a file moves</h2>
      <PipelineDiagram />
      <p>
        Outbound, the API builds the fixed-width delivery in-process and returns <code>202</code> as soon as it
        is written to S3 — the transfer to the bank is a separate Lambda, so a slow SFTP session never becomes
        a slow API call. Inbound, a poller fetches (and deletes) what is in the mailbox on a schedule; each
        object landing in the bucket triggers the processor, which maps <b>one file to one event</b> and does no
        per-record splitting.
      </p>
      <p>
        There is one such path per <b>mailbox</b> — an SFTP login at the bank, with the subsystem the bank
        agreed for it (<code>BS1</code>, <code>BS2</code>). The caller's <code>creditor.subsystem</code> picks
        the mailbox a delivery ships through, the mailbox a file was fetched from is stamped on its event as{" "}
        <code>subsystem</code>, and each has its own folder in the buckets (<code>inbound/bs2/</code>,{" "}
        <code>outbound/bs2/</code>), its own poll and transfer Lambdas, and its own secrets. The creditor
        cannot tell them apart: the same PBS number can be registered under both.
      </p>
      <p>
        Both buckets keep every byte that crossed them — the delivery exactly as it was shipped, and the bank's
        answer exactly as it arrived. That is what the file list on the other tab is reading.
      </p>
    </section>
  );
}

function Files({
  counts,
  mailboxes,
  onOpenFiles,
}: {
  counts: Counts;
  mailboxes: MailboxInfo[];
  onOpenFiles: (mailbox: Mailbox, direction: Direction, kind: ContentKind) => void;
}): JSX.Element {
  // With one login there is one mailbox to name; with several, each count says
  // whose it is.
  const several = mailboxes.length > 1;
  return (
    <section id="files" className="doc-section">
      <h2>What is in the mailbox</h2>
      <p>
        Nine kinds of file can turn up, and the service has a considered answer for each. The counts are what is
        in the buckets right now{several ? ", per login" : ""} — click one to see them.
      </p>

      {FILE_KIND_ORDER.map((kind) => {
        const doc = FILE_KINDS[kind];
        const perMailbox = counts.byKind[kind] ?? [];
        return (
          <div className="kind-card" key={kind} id={`kind-${kind}`}>
            <div className="kind-head">
              <span className="kind-badge" data-kind={kind}>
                {doc.badge}
              </span>
              <h3>{doc.title}</h3>
              <span className="chip" data-tone="quiet">
                {doc.direction === "inbound" ? "from the bank" : "to the bank"}
              </span>
              <span className="spacer" />
              {counts.state === "counted" && perMailbox.length > 0 ? (
                <span className="kind-counts">
                  {perMailbox.map(({ mailbox, count }) => (
                    <button
                      key={mailbox.id}
                      type="button"
                      className="linkish"
                      onClick={() => onOpenFiles(mailbox.id, doc.direction, kind)}
                    >
                      {count} in {several ? mailboxLabel(mailbox) : "the mailbox"} →
                    </button>
                  ))}
                </span>
              ) : counts.state === "counted" ? (
                <span className="dim">none right now</span>
              ) : null}
            </div>

            <p>{doc.what}</p>
            <p className="kind-handling">
              <span className="label">What we do with it</span>
              {doc.handling}
            </p>

            {doc.codes ? (
              <dl className="code-list">
                {doc.codes.map((code) => (
                  <div key={code.code}>
                    <dt className="code-chip">{code.code}</dt>
                    <dd>{code.means}</dd>
                  </div>
                ))}
              </dl>
            ) : null}

            <div className="kind-foot">
              {doc.event ? (
                <span>
                  <span className="label">Event</span> <code>{doc.event}</code>
                </span>
              ) : null}
              {doc.operations ? (
                <span>
                  <span className="label">Written by</span>{" "}
                  {doc.operations.map((operation) => (
                    <code key={operation}>{operation}</code>
                  ))}
                </span>
              ) : null}
              {doc.guideline ? (
                <span>
                  <span className="label">Specified in</span> <code>{doc.guideline}</code>
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function Names(): JSX.Element {
  return (
    <section id="names" className="doc-section">
      <h2>Reading a file name</h2>
      <p>
        Mastercard Payment Services names every file in the mailbox by a fixed convention, so the name is data:
        the first letter says what kind of file it is, the digits are the bank's delivery number, and the suffix
        is the delivery type. <code>D3143191001-602.BS2</code> is the data file of delivery 3143191001, a BS
        0602. The file list and each file's header break the name apart rather than printing it whole.
      </p>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Prefix</th>
              <th>Danish</th>
              <th>What it is</th>
            </tr>
          </thead>
          <tbody>
            {NAME_PREFIXES.map((prefix) => (
              <tr key={prefix.letter}>
                <td>{prefix.letter}</td>
                <td>{prefix.danish}</td>
                <td className="prose">{prefix.means}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Records(): JSX.Element {
  return (
    <section id="records" className="doc-section">
      <h2>Reading a record</h2>
      <p>
        A delivery is fixed-width text in ISO-8859-1: one record per line, every field at a known character
        position. Positions 3–5 give the record type, and the section it sits in decides which layout applies —
        which is why the same <code>042</code> record means a collection in a BS 0601 and a settled payment in a
        BS 0602.
      </p>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Record</th>
              <th>What it carries</th>
            </tr>
          </thead>
          <tbody>
            {RECORD_TYPES.map((record) => (
              <tr key={record.type}>
                <td>{record.type}</td>
                <td className="prose">{record.means}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        The <b>Records</b> view of any file lays each line out under a character ruler and names every field
        from the same layout registry the parsers use — so what you read there is what the service read. Where a
        delivery declares its own totals on the <code>992</code> record, they are summed against the records and
        a mismatch is reported: that is how a wrong field position gets caught, and how the §0215 amount bug was
        found.
      </p>
    </section>
  );
}

function Api({ onOpenReference }: { onOpenReference: () => void }): JSX.Element {
  return (
    <section id="api" className="doc-section">
      <h2>The API</h2>
      <p>
        Everything a caller can ask for. It speaks collections and mandates and never mentions a file — the
        whole of the fixed-width exchange happens behind these six operations. Calls are signed with IAM
        (SigV4) and the resource policy admits only principals in this AWS account, so the public DNS is
        useless without a role. The table is generated from the OpenAPI contract, so it cannot claim an
        endpoint that does not exist.
      </p>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Method</th>
              <th>Path</th>
              <th>What it does</th>
            </tr>
          </thead>
          <tbody>
            {domainApi.operations.map((operation) => (
              <tr key={operation.operationId}>
                <td>{operation.method}</td>
                <td>{operation.path}</td>
                <td className="prose">{operation.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="doc-more">
        <button type="button" className="linkish" onClick={onOpenReference}>
          Every parameter, request and response, in the API reference →
        </button>
      </p>
    </section>
  );
}

function Events({ onOpenReference }: { onOpenReference: () => void }): JSX.Element {
  return (
    <section id="events" className="doc-section">
      <h2>The events</h2>
      <p>
        Everything the bank tells us leaves as an EventBridge event on the <code>betalingsservice</code> bus —
        one per inbound file, with <code>detail-type</code> equal to the event type and the domain event as{" "}
        <code>detail</code>. Consumers subscribe with rules; there is nothing to register through the API.
      </p>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Event</th>
              <th>Schema</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {domainEvents.map((event) => (
              <tr key={event.type}>
                <td>{event.type}</td>
                <td>{event.schema}</td>
                <td className="prose">{event.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        <b>One delivery, several creditors.</b> The bank packs a section per creditor into a single file, so an
        event routinely spans more than one. Every line item carries its own <code>creditor</code>, and customer
        and mandate numbers are only unique within one — resolve a debtor on{" "}
        <code>(creditor.pbsNumber, debtor.customerNumber)</code>, never on the customer number alone.
      </p>
      <p className="doc-more">
        <button type="button" className="linkish" onClick={onOpenReference}>
          Each event's payload, field by field, in the API reference →
        </button>
      </p>
    </section>
  );
}

function Dates({ calendars, token }: { calendars: Guideline[]; token: string }): JSX.Element {
  const [shown, setShown] = useState(0);
  const [full, setFull] = useState(false);
  const active = calendars[shown];
  const image = useCalendarImage(active?.slug, token);

  return (
    <section id="dates" className="doc-section">
      <h2>Dates and deadlines</h2>
      <p>
        Everything in Betalingsservice is counted in <b>banking days</b> — every day except Saturdays, Sundays,
        public holidays, the Friday after Ascension Day, and Constitution Day. The bank publishes which days
        those are, half a year at a time, and the calendar below is that publication: bank-closed days are the
        ones marked in red.
      </p>
      <p>
        Payment data is due by <b>09:00 on the sixth-last banking day</b> of the month, and the validation
        receipt has to carry a timestamp before <b>18:30</b> for the delivery to be processed without an extra
        fee. <i>Ekstraadvisering</i> — a chargeable late window — runs from the sixth-last banking day at 18:30
        to the third-last at 18:30.
      </p>
      <p>
        <b>This service enforces none of that.</b> Date rules belong to the caller: the only check it makes is
        that a due date is not in the past. A due date the bank will not accept comes back as a rejected
        outcome inside a <code>payment.advice</code>, or as a <code>batch.rejected</code>.
      </p>

      {calendars.length > 0 ? (
        <>
          <div className="views" role="tablist" aria-label="Which half-year">
            {calendars.map((calendar, index) => (
              <button
                key={calendar.slug}
                type="button"
                role="tab"
                aria-selected={index === shown}
                onClick={() => setShown(index)}
              >
                {calendar.title.replace(/^.*— /, "")}
              </button>
            ))}
          </div>

          <figure className="calendar">
            {image.url ? (
              // Half a year of dates in one image: readable across the article at
              // best, so it also opens at the size the bank published it and
              // scrolls, rather than being squinted at.
              <div className="calendar-frame" data-full={full}>
                <img
                  src={image.url}
                  alt={active?.title ?? "The bank's calendar"}
                  title={full ? "Fit to the page" : "See it at full size"}
                  onClick={() => setFull(!full)}
                />
              </div>
            ) : (
              <p className={image.error ? "error" : "dim"}>{image.error ?? "Loading the calendar…"}</p>
            )}
            <figcaption>
              <button type="button" className="linkish" onClick={() => setFull(!full)}>
                {full ? "Fit to the page" : "See it at full size"}
              </button>{" "}
              ·{" "}
              Published by Mastercard Payment Services. This copy is only as current as the last deploy — the
              newest editions, and the cutoffs that go with them, are at{" "}
              <a href={CALENDAR_SOURCE} target="_blank" rel="noreferrer noopener">
                betalingsservice.dk → Kalendere &amp; tidsfrister
              </a>
              .
            </figcaption>
          </figure>
        </>
      ) : (
        <p className="dim">No calendar ships with this deployment.</p>
      )}
    </section>
  );
}

function Guidelines({ guidelines, token }: { guidelines: GuidelineState; token: string }): JSX.Element {
  return (
    <section id="guidelines" className="doc-section">
      <h2>The bank's guidelines</h2>
      <p>
        Mastercard Payment Services' own documentation, shipped with the service and served from behind the same
        sign-in as the files — the record layouts every parser here is written to, the operational rules, and the
        SFTP guides. Every field description in the Records view comes out of the first document below.
      </p>

      {guidelines.error ? <p className="error">{guidelines.error}</p> : null}
      {!guidelines.loaded && !guidelines.error ? <p className="dim">Loading the library…</p> : null}

      {GROUP_ORDER.map((group) => {
        const inGroup = guidelines.items.filter((guideline) => guideline.group === group);
        if (inGroup.length === 0) return null;
        return (
          <div key={group}>
            <div className="section-title">
              <h3>{GROUP_TITLES[group]}</h3>
            </div>
            <div className="guideline-list">
              {inGroup.map((guideline) => (
                <GuidelineRow key={guideline.slug} guideline={guideline} token={token} />
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function GuidelineRow({ guideline, token }: { guideline: Guideline; token: string }): JSX.Element {
  const [state, setState] = useState<"idle" | "fetching" | "failed">("idle");

  async function save(): Promise<void> {
    setState("fetching");
    try {
      saveBlob(await fetchGuideline(token, guideline.slug), `${guideline.slug}.${guideline.format}`);
      setState("idle");
    } catch {
      setState("failed");
    }
  }

  return (
    <div className="guideline">
      <div className="guideline-head">
        <h4>{guideline.title}</h4>
        {guideline.deliveryType ? <span className="code-chip">BS {guideline.deliveryType}</span> : null}
        <span className="chip" data-tone="quiet">
          {guideline.language === "da" ? "Danish" : "English"}
        </span>
        <span className="spacer" />
        <button type="button" className="linkish" onClick={() => void save()} disabled={state === "fetching"}>
          {state === "fetching" ? "Fetching…" : guideline.format === "png" ? "Download image" : "Download PDF"}
        </button>
      </div>
      <p>{guideline.summary}</p>
      <p className="guideline-meta">
        {guideline.pages} page{guideline.pages === 1 ? "" : "s"} · {formatSize(guideline.size)}
        {guideline.source ? (
          <>
            {" · "}
            <a href={guideline.source} target="_blank" rel="noreferrer">Official source</a>
          </>
        ) : null}
        {state === "failed" ? " · that download failed" : ""}
      </p>
    </div>
  );
}

// ── Live data ───────────────────────────────────────────────────────────────

const railStatus = (counts: Counts): string => {
  if (counts.state === "counting") return "Counting the mailbox…";
  if (counts.state === "failed") return "The mailbox could not be counted";
  return `${counts.inbound} received · ${counts.outbound} sent`;
};

/**
 * How many of each kind are in the buckets, counted from a listing of both
 * directions of every mailbox.
 */
function useLiveCounts(token: string, mailboxes: MailboxInfo[]): Counts {
  const [counts, setCounts] = useState<Counts>({ byKind: {}, inbound: 0, outbound: 0, state: "counting" });

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      mailboxes.map(async (mailbox) => {
        const [inbound, outbound] = await Promise.all([
          listFiles(token, mailbox.id, "inbound", { limit: 200 }),
          listFiles(token, mailbox.id, "outbound", { limit: 200 }),
        ]);
        return { mailbox, inbound: inbound.files, outbound: outbound.files };
      }),
    )
      .then((listings) => {
        if (cancelled) return;
        const byKind: Partial<Record<ContentKind, MailboxCount[]>> = {};
        let inboundTotal = 0;
        let outboundTotal = 0;
        for (const { mailbox, inbound, outbound } of listings) {
          inboundTotal += inbound.length;
          outboundTotal += outbound.length;
          const here: Partial<Record<ContentKind, number>> = {};
          for (const file of [...inbound, ...outbound]) here[file.contentKind] = (here[file.contentKind] ?? 0) + 1;
          for (const [kind, count] of Object.entries(here) as Array<[ContentKind, number]>) {
            (byKind[kind] ??= []).push({ mailbox, count });
          }
        }
        setCounts({ byKind, inbound: inboundTotal, outbound: outboundTotal, state: "counted" });
      })
      .catch(() => {
        // A count is a nicety; the documentation reads fine without one, and
        // saying nothing beats showing "none" for files that may well be there.
        if (!cancelled) setCounts({ byKind: {}, inbound: 0, outbound: 0, state: "failed" });
      });
    return () => {
      cancelled = true;
    };
  }, [token, mailboxes]);

  return counts;
}

/**
 * One calendar's bytes as something an <img> can show. Fetched with the token
 * like everything else here, so it arrives as a blob rather than a URL the
 * browser could load on its own.
 */
function useCalendarImage(slug: string | undefined, token: string): { url?: string; error?: string } {
  const [state, setState] = useState<{ url?: string; error?: string }>({});

  useEffect(() => {
    if (slug === undefined) return;
    let url: string | undefined;
    let cancelled = false;
    setState({});
    fetchGuideline(token, slug)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setState({ url });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ error: `The calendar could not be loaded: ${message(error)}` });
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [slug, token]);

  return state;
}

const message = (error: unknown): string => (error instanceof Error ? error.message : String(error));

interface GuidelineState {
  items: Guideline[];
  loaded: boolean;
  error?: string;
}

function useGuidelines(token: string): GuidelineState {
  const [state, setState] = useState<GuidelineState>({ items: [], loaded: false });


  useEffect(() => {
    let cancelled = false;
    listGuidelines(token)
      .then((items) => {
        if (!cancelled) setState({ items, loaded: true });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            items: [],
            loaded: true,
            error: `The guideline library could not be read: ${message(error)}`,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return state;
}
